import { useState, useEffect, useCallback } from 'react';
import type { FavoritesMap, Favorite, VesselPosition, BaseVesselData, StaticVesselData } from '../types/vessel';

const FAVORITES_KEY = 'favorites';
const HIDDEN_GROUPS_KEY = 'hiddenGroups';
const EMPTY_GROUPS_KEY = 'emptyGroups';

export function useFavorites() {
  const [favorites, setFavorites] = useState<FavoritesMap>(() => {
    const stored = localStorage.getItem(FAVORITES_KEY);
    return stored ? JSON.parse(stored) : {};
  });

  const [hiddenGroups, setHiddenGroups] = useState<Record<string, boolean>>(() => {
    const stored = localStorage.getItem(HIDDEN_GROUPS_KEY);
    return stored ? JSON.parse(stored) : {};
  });

  const [emptyGroups, setEmptyGroups] = useState<string[]>(() => {
    const stored = localStorage.getItem(EMPTY_GROUPS_KEY);
    return stored ? JSON.parse(stored) : [];
  });

  useEffect(() => {
    localStorage.setItem(FAVORITES_KEY, JSON.stringify(favorites));
  }, [favorites]);

  useEffect(() => {
    localStorage.setItem(HIDDEN_GROUPS_KEY, JSON.stringify(hiddenGroups));
  }, [hiddenGroups]);

  useEffect(() => {
    localStorage.setItem(EMPTY_GROUPS_KEY, JSON.stringify(emptyGroups));
  }, [emptyGroups]);

  const toggleFavorite = useCallback((
    vesselId: string,
    vessel: VesselPosition,
    baseData?: BaseVesselData,
    staticData?: StaticVesselData
  ) => {
    setFavorites((prev) => {
      if (prev[vesselId]) {
        const { [vesselId]: _, ...rest } = prev;
        return rest;
      }
      const name = vessel.Name || baseData?.Name || staticData?.Name || 'Unknown';
      const newFav: Favorite = {
        id: vesselId,
        name,
        flag: vessel.Flag || staticData?.Flag || '',
        type: baseData?.Type || staticData?.ShipTypeLevel5 || 'LNG',
        group: 'default',
      };
      return { ...prev, [vesselId]: newFav };
    });
  }, []);

  const toggleGroupVisibility = useCallback((groupName: string) => {
    setHiddenGroups((prev) => {
      if (prev[groupName]) {
        const { [groupName]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [groupName]: true };
    });
  }, []);

  const moveToGroup = useCallback((vesselId: string, groupName: string) => {
    setFavorites((prev) => {
      if (!prev[vesselId]) return prev;
      return { ...prev, [vesselId]: { ...prev[vesselId], group: groupName } };
    });
  }, []);

  const createGroup = useCallback((groupName: string) => {
    if (!groupName.trim()) return;
    const trimmed = groupName.trim();
    // Check if group already exists in favorites
    const existsInFavorites = Object.values(favorites).some(f => f.group === trimmed);
    if (!existsInFavorites && !emptyGroups.includes(trimmed)) {
      setEmptyGroups(prev => [...prev, trimmed]);
    }
  }, [favorites, emptyGroups]);

  const renameGroup = useCallback((oldName: string, newName: string) => {
    if (!newName.trim() || oldName === newName) return;
    const trimmedNew = newName.trim();
    
    // Update favorites
    setFavorites((prev) => {
      const updated = { ...prev };
      Object.keys(updated).forEach((key) => {
        if (updated[key].group === oldName) {
          updated[key] = { ...updated[key], group: trimmedNew };
        }
      });
      return updated;
    });
    
    // Update empty groups
    setEmptyGroups(prev => prev.map(g => g === oldName ? trimmedNew : g));
    
    // Update hidden groups
    setHiddenGroups((prev) => {
      if (prev[oldName]) {
        const { [oldName]: wasHidden, ...rest } = prev;
        return { ...rest, [trimmedNew]: wasHidden };
      }
      return prev;
    });
  }, []);

  const deleteGroup = useCallback((groupName: string) => {
    // Remove all favorites in this group (delete them, not move to default)
    setFavorites((prev) => {
      const updated = { ...prev };
      Object.keys(updated).forEach((key) => {
        if (updated[key].group === groupName) {
          delete updated[key];
        }
      });
      return updated;
    });
    
    // Remove from empty groups
    setEmptyGroups(prev => prev.filter(g => g !== groupName));
    
    // Remove from hidden groups
    setHiddenGroups((prev) => {
      const { [groupName]: _, ...rest } = prev;
      return rest;
    });
  }, []);

  const isFavorite = useCallback((vesselId: string) => !!favorites[vesselId], [favorites]);

  return {
    favorites,
    hiddenGroups,
    emptyGroups,
    toggleFavorite,
    toggleGroupVisibility,
    moveToGroup,
    createGroup,
    renameGroup,
    deleteGroup,
    isFavorite,
  };
}
